/**
 * SaveTemplateModal：「设置模板」二级弹窗（纯 UI + 表单校验）。
 *
 * 职责边界：
 * - 收集模板类型（template_category）与名称（name_cn），校验通过后通过 onSubmit 回传；
 * - 不直接调 saveAiPrompt，上下文（gameCode / dashboardId / cardId）由父层 AiSettings/index 注入。
 *
 * 产品约束：模板类型按 privilegesConfig 权限裁剪；无权限的类型不展示（personal 均可；game 需 manager/admin；public 需 sys_admin）。
 */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Radio, notification } from 'tea-component';
import { shallowEqual, useIntl, useSelector } from 'umi';

import type { TAiPromptTemplateCategory } from '@/pages/datalab/services/ai/api';
import {
  AI_TEMPLATE_HINT_I18N_KEY,
  AI_TEMPLATE_LABEL_I18N_KEY,
  AI_TEMPLATE_NAME_MAX_LENGTH,
  canSaveAiTemplateCategory,
  getAllowedAiTemplateCategories,
  resolveAllowedTemplateCategory,
  resolveTemplateNameValidation,
  type IExistingAiTemplateNameRef,
} from './templateConstants';
import './SaveTemplateModal.scss';

/** 模板保存表单提交载荷，由父层 handleSaveTemplate 映射为 prompts/save 请求体 */
export interface ISaveTemplateModalSubmitPayload {
  name: string;
  templateCategory: TAiPromptTemplateCategory;
  /** 当前设置页的分析思路正文，对应 prompt_content */
  promptContent: string;
  /** 分析背景，对应 analysis_context */
  analysisContext: string;
  /** 是否自定义思路，对应 is_custom */
  isCustom: boolean;
}

export interface ISaveTemplateModalProps {
  visible: boolean;
  submitting?: boolean;
  popupContainer?: HTMLElement | null;
  onClose: () => void;
  onSubmit: (payload: ISaveTemplateModalSubmitPayload) => Promise<void>;
  promptContent: string;
  analysisContext: string;
  isCustom: boolean;
  /** 打开弹窗时默认选中的模板类型，来自当前分析思路的 template_category */
  defaultTemplateCategory?: TAiPromptTemplateCategory;
  /** 打开弹窗时默认填充的模板名称，来自当前分析思路的 name_cn */
  defaultTemplateName?: string;
  /** 分析思路列表中已有模板名称，用于同类型下重名提示（不拦截保存） */
  existingTemplates?: IExistingAiTemplateNameRef[];
  /** 当前登录用户 id，personal 重名仅与本人 list 项比对 */
  currentUserId?: string;
}

const SaveTemplateModal: React.FC<ISaveTemplateModalProps> = (props) => {
  const {
    visible,
    submitting = false,
    popupContainer,
    onClose,
    onSubmit,
    promptContent,
    analysisContext,
    isCustom,
    defaultTemplateCategory = 'personal',
    defaultTemplateName = '',
    existingTemplates = [],
    currentUserId = '',
  } = props;
  const { formatMessage } = useIntl();
  const templatePermissions = useSelector(
    (state: {
      privilegesConfig?: {
        isPermissionAdmin?: boolean;
        isPermissionManager?: boolean;
      };
    }) => ({
      isPermissionAdmin: Boolean(state.privilegesConfig?.isPermissionAdmin),
      isPermissionManager: Boolean(state.privilegesConfig?.isPermissionManager),
    }),
    shallowEqual,
  );
  const allowedTemplateCategories = useMemo(
    () => getAllowedAiTemplateCategories(templatePermissions),
    [templatePermissions],
  );
  const [templateCategory, setTemplateCategory] = useState<TAiPromptTemplateCategory>('personal');
  const [templateName, setTemplateName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [nameWarning, setNameWarning] = useState<string>('');

  // 统一入口：输入/切类型/打开弹窗时同步 error 与 warning，保证实时校验口径一致
  const applyTemplateNameValidation = useCallback(
    (value: string, category: TAiPromptTemplateCategory): void => {
      const { error, warning } = resolveTemplateNameValidation(
        value,
        category,
        formatMessage,
        existingTemplates,
        currentUserId,
      );
      setNameError(error);
      setNameWarning(warning);
    },
    [currentUserId, existingTemplates, formatMessage],
  );

  // 每次打开弹窗预填类型与名称；无权限的类型回退为 personal
  useEffect(() => {
    if (!visible) {
      return;
    }
    const category = resolveAllowedTemplateCategory(
      defaultTemplateCategory,
      templatePermissions,
    );
    setTemplateName(defaultTemplateName);
    setTemplateCategory(category);
    applyTemplateNameValidation(defaultTemplateName, category);
  }, [
    applyTemplateNameValidation,
    defaultTemplateCategory,
    defaultTemplateName,
    templatePermissions,
    visible,
  ]);

  const hintText = useMemo(() => {
    if (!templateCategory) {
      return '';
    }
    return formatMessage({ id: AI_TEMPLATE_HINT_I18N_KEY[templateCategory] });
  }, [formatMessage, templateCategory]);

  const handleNameChange = useCallback(
    (value: string) => {
      setTemplateName(value);
      applyTemplateNameValidation(value, templateCategory);
    },
    [applyTemplateNameValidation, templateCategory],
  );

  const handleCategoryChange = useCallback(
    (value: string) => {
      const category = value as TAiPromptTemplateCategory;
      setTemplateCategory(category);
      applyTemplateNameValidation(templateName, category);
    },
    [applyTemplateNameValidation, templateName],
  );

  const isCategoryAllowed = useCallback(
    (category: TAiPromptTemplateCategory): boolean =>
      canSaveAiTemplateCategory(category, templatePermissions),
    [templatePermissions],
  );

  // 提交成功后由本层展示通知并关闭；失败通知在此处理，接口异常向上抛出供父层 finally 收尾
  const handleConfirm = useCallback(async () => {
    if (submitting) {
      return;
    }
    const { error: nextError, warning: nextWarning } =
      resolveTemplateNameValidation(
        templateName,
        templateCategory,
        formatMessage,
        existingTemplates,
        currentUserId,
      );
    setNameError(nextError);
    setNameWarning(nextWarning);
    if (nextError) {
      return;
    }
    if (!isCategoryAllowed(templateCategory)) {
      notification.error({
        description: formatMessage({ id: 'dbl_ai_analysis_template_category_forbidden' }),
      });
      return;
    }
    try {
      await onSubmit({
        name: templateName.trim(),
        templateCategory,
        promptContent,
        analysisContext,
        isCustom,
      });
      notification.success({
        description: formatMessage({ id: 'dbl_ai_analysis_template_save_success' }),
      });
      onClose();
    } catch (_error) {
      notification.error({
        description: formatMessage({ id: 'dbl_ai_analysis_template_save_fail' }),
      });
    }
  }, [
    analysisContext,
    currentUserId,
    formatMessage,
    isCustom,
    onClose,
    onSubmit,
    promptContent,
    templateCategory,
    existingTemplates,
    templateName,
    isCategoryAllowed,
    submitting,
  ]);

  // 保存中仅主按钮展示 loading，不灰置类型/名称等表单项，便于用户继续查看或取消
  const isConfirmDisabled = Boolean(nameError) || !templateName.trim();

  return (
    <Modal
      visible={visible}
      caption={formatMessage({ id: 'dbl_ai_analysis_template_modal_title' })}
      onClose={onClose}
      className="dbl-ai-analysis-save-template-modal"
      popupContainer={popupContainer || undefined}
    >
      <Modal.Body>
        <div className="dbl-ai-analysis-save-template-modal__field">
          <div className="dbl-ai-analysis-save-template-modal__label">
            {formatMessage({ id: 'dbl_ai_analysis_template_type_label' })}
          </div>
          <Radio.Group
            value={templateCategory}
            onChange={handleCategoryChange}
          >
            {allowedTemplateCategories.map((category) => (
              <Radio key={category} name={category}>
                {formatMessage({ id: AI_TEMPLATE_LABEL_I18N_KEY[category] })}
              </Radio>
            ))}
          </Radio.Group>
          {hintText ? (
            <div className="dbl-ai-analysis-save-template-modal__hint">
              {hintText}
            </div>
          ) : null}
        </div>

        <div className="dbl-ai-analysis-save-template-modal__field">
          <div className="dbl-ai-analysis-save-template-modal__label dbl-ai-analysis-save-template-modal__label--required">
            {formatMessage({ id: 'dbl_ai_analysis_template_name_label' })}
          </div>
          <Input
            size="full"
            value={templateName}
            maxLength={AI_TEMPLATE_NAME_MAX_LENGTH}
            placeholder={formatMessage({ id: 'dbl_ai_analysis_template_name_placeholder' })}
            onChange={handleNameChange}
          />
          {nameError ? (
            <div className="dbl-ai-analysis-save-template-modal__error">
              {nameError}
            </div>
          ) : null}
          {nameWarning ? (
            <div className="dbl-ai-analysis-save-template-modal__warning">
              {nameWarning}
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          loading={submitting}
          disabled={isConfirmDisabled}
          onClick={handleConfirm}
        >
          {formatMessage({ id: 'dbl_ai_analysis_settings_action_confirm' })}
        </Button>
        <Button type="weak" onClick={onClose}>
          {formatMessage({ id: 'dbl_ai_analysis_settings_action_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(SaveTemplateModal);
